import { LevelId } from '../sim/levels';
import { PROGRESSION_CONFIG } from './config';
import { DrillId, Stars } from './drill';

/** Everything the player has earned. Plain data: JSON-serialisable, versioned, changed only by the reducers below. */
export interface Progress {
  version: 1;
  drills: Record<DrillId, Stars>;
  track: { bestLap: number | null; bestTrial: number | null; trialsFinished: number };
  enduro: { bestTime: number | null; finished: number };
}

export const emptyProgress = (): Progress => ({
  version: 1,
  drills: { throttle: 0, brake: 0, figure8: 0 },
  track: { bestLap: null, bestTrial: null, trialsFinished: 0 },
  enduro: { bestTime: null, finished: 0 },
});

const best = (a: number | null, b: number) => (a === null ? b : Math.min(a, b));
const clone = (p: Progress): Progress => sanitizeProgress(JSON.parse(JSON.stringify(p)));

export function recordDrill(p: Progress, id: DrillId, stars: Stars): Progress {
  const n = clone(p);
  n.drills[id] = Math.max(n.drills[id], stars) as Stars;
  return n;
}

export function recordLap(p: Progress, lapTime: number): Progress {
  const n = clone(p);
  n.track.bestLap = best(n.track.bestLap, lapTime);
  return n;
}

/** A time-trial run; it counts as finished only with all laps done. */
export function recordTimeTrial(p: Progress, laps: readonly number[]): Progress {
  let n = laps.reduce(recordLap, p);
  if (laps.length < PROGRESSION_CONFIG.timeTrialLaps) return n;
  n = clone(n);
  n.track.trialsFinished++;
  n.track.bestTrial = best(n.track.bestTrial, laps.reduce((a, b) => a + b, 0));
  return n;
}

export function recordEnduro(p: Progress, total: number): Progress {
  const n = clone(p);
  n.enduro.finished++;
  n.enduro.bestTime = best(n.enduro.bestTime, total);
  return n;
}

/** Ring is always open; all drills at the minimum stars open the track; a fast lap or a finished trial opens enduro. */
export function isUnlocked(p: Progress, level: LevelId): boolean {
  if (level === 'ring') return true;
  const ringDone = Object.values(p.drills).every((s) => s >= PROGRESSION_CONFIG.trackMinStars);
  if (level === 'track') return ringDone;
  const t = p.track;
  return ringDone && (t.trialsFinished > 0 || (t.bestLap !== null && t.bestLap < PROGRESSION_CONFIG.enduroLapTime));
}

export const unlockedLevels = (p: Progress): LevelId[] => (['ring', 'track', 'enduro'] as const).filter((l) => isUnlocked(p, l));

/** Levels that `after` unlocks and `before` did not: what the UI announces. */
export const newlyUnlocked = (before: Progress, after: Progress): LevelId[] =>
  unlockedLevels(after).filter((l) => !isUnlocked(before, l));

const obj = (v: unknown): Record<string, unknown> =>
  (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const time = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null);
const count = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : 0);
const stars = (v: unknown): Stars =>
  (typeof v === 'number' && Number.isFinite(v) ? (Math.max(0, Math.min(3, Math.round(v))) as Stars) : 0);

/** Valid progress from anything: stored JSON may be old, hand-edited or corrupt. */
export function sanitizeProgress(raw: unknown): Progress {
  const r = obj(raw), d = obj(r.drills), t = obj(r.track), e = obj(r.enduro);
  return {
    version: 1,
    drills: { throttle: stars(d.throttle), brake: stars(d.brake), figure8: stars(d.figure8) },
    track: { bestLap: time(t.bestLap), bestTrial: time(t.bestTrial), trialsFinished: count(t.trialsFinished) },
    enduro: { bestTime: time(e.bestTime), finished: count(e.finished) },
  };
}
